import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { IUser } from '../../../interfaces/IUser.interface';
import { UsersService } from '../../../services/users.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IPhysician } from '../../../interfaces/IPhysician.interface';
import { PhysiciansService } from '../../../services/physicians.service';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import {
  MatCard,
  MatCardContent,
  MatCardFooter,
  MatCardHeader,
} from '@angular/material/card';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { ResponsiveService } from '../../../services/responsive.service';
import { MatTooltip } from '@angular/material/tooltip';
import { NotificationsService } from '../../../services/notifications.service';
import { MatBadge } from '@angular/material/badge';
import { NotificationsComponent } from '../notifications/notifications.component';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatBadgeIconDirective } from '../../../directives/mat-badge-icon.directive';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { ConfigService } from '../../../core/services/config.service';
import { DialogService } from '../../../services/dialog.service';
import { IDialog } from '../../../interfaces/IDialog.interface';
import { LoadingIndicator } from '../loading-indicator/loading-indicator.component';
import { StaticDataService } from '../../../services/static-data.service';

@Component({
  selector: 'app-user-details-card',
  imports: [
    MatIcon,
    LoadingIndicator,
    TranslatePipe,
    MatIconButton,
    MatCard,
    MatCardContent,
    MatCardHeader,
    MatCardFooter,
    MatTooltip,
    MatBadge,
    MatMenuTrigger,
    MatMenu,
    NotificationsComponent,
    MatBadgeIconDirective,
    NgxSkeletonLoaderComponent,
  ],
  templateUrl: './user-details-card.component.html',
  styleUrl: './user-details-card.component.scss',
})
export class UserDetailsCardComponent implements OnInit {
  protected user: IUser | undefined = undefined;
  private readonly _usersService: UsersService = inject(UsersService);
  private readonly _responsiveService = inject(ResponsiveService);
  private readonly _physiciansService: PhysiciansService =
    inject(PhysiciansService);
  private readonly _featureFlagService = inject(FeatureFlagService);
  private readonly _notificationsService = inject(NotificationsService);
  private readonly _translateService: TranslateService =
    inject(TranslateService);
  private readonly _configService: ConfigService = inject(ConfigService);
  private readonly _dialogService: DialogService = inject(DialogService);
  private readonly _staticDataService: StaticDataService =
    inject(StaticDataService);

  protected readonly _kammerName = this._configService.kammerName;

  protected isXSmallViewport = computed(() =>
    this._responsiveService.isMobile(),
  );
  protected loading = signal<boolean>(false);

  protected showNotifications = signal<boolean>(false);
  protected unreadNotificationsCount = this._notificationsService.count;

  protected isLogoutButtonEnabled =
    this._featureFlagService.isLogoutButtonEnabled;

  protected physician: IPhysician | undefined = undefined;

  async ngOnInit() {
    this.showNotifications.set(
      this._featureFlagService.isNotificationsEnabled(),
    );

    this._usersService.getUser().then((user) => {
      this.user = user;
    });

    this.userIsPhysician();
  }

  public getKeycloakAccount() {
    const url = this._usersService.createAccountUrl({
      redirectUri: window.location.href,
    });
    window.location.replace(url);
  }

  public logout() {
    this._usersService.logout({
      redirectUri: window.location.origin,
    });
  }

  protected async showAgbs() {
    const loading = signal<boolean>(true);
    this._dialogService.openLoading(loading);
    const content = await this._staticDataService.getAGBs();
    loading.set(false);
    const data: IDialog = {
      message: content,
      icon: 'assignment_globe',
      buttonLabel: this._translateService.instant('buttons.close'),
    };
    this._dialogService.openDialogSimple(data);
  }

  private userIsPhysician() {
    if (this._featureFlagService.isMemberShipEnabled()) {
      this._physiciansService.getPhysician().then((physicians) => {
        if (physicians) {
          this.physician = physicians;
        }
      });
    }
  }

  protected async loadNotifications() {
    if (!this.showNotifications()) {
      return;
    }
    this.loading.set(true);
    await this._notificationsService.getNotifications();
    this.loading.set(false);
  }
}
